import { createAirport } from '../services/api.js';

/**
 * Airport Combo Box Component
 * Combo box that shows a dropdown on focus and filters on every keystroke.
 * Replaces the airport-autocomplete with richer behaviour: focus-triggered
 * dropdown, no minimum character count, keyboard navigation, and free-text entry.
 *
 * @typedef {Object} ComboBoxConfig
 * @property {string} name - Input field name
 * @property {string} label - Label text
 * @property {string} [aircraftId] - For favourite airports lookup
 * @property {Function} onSelect - (airport) => void
 * @property {string} [value] - Initial value for edit mode
 * @property {Array<{icao_code: string, name: string}>} [airports] - Full airport list
 * @property {Array<{icao_code: string, name: string}>} [favourites] - Favourite airports for aircraft
 */

const MAX_RESULTS = 10;

/**
 * Format an airport for display: "ICAO — Airport Name"
 * @param {{icao_code: string, name: string}} airport
 * @returns {string}
 */
export function formatAirport(airport) {
  return `${airport.icao_code} — ${airport.name}`;
}

/**
 * Filter airports by query string (case-insensitive match on icao_code or name).
 * @param {Array<{icao_code: string, name: string}>} airports
 * @param {string} query
 * @returns {Array<{icao_code: string, name: string}>}
 */
export function filterAirports(airports, query) {
  if (!query) return airports;
  const lower = query.toLowerCase();
  return airports.filter((a) => {
    const code = (a.icao_code || '').toLowerCase();
    const name = (a.name || '').toLowerCase();
    return code.includes(lower) || name.includes(lower);
  });
}

/**
 * createAirportComboBox(config) → HTMLElement
 *
 * Returns a combo box element. On focus: shows favourites (if aircraftId set)
 * or first 10 airports alphabetically. On keystroke: filters by ICAO code or
 * name (case-insensitive). Max 10 results shown with "Type more to narrow results"
 * hint. Supports keyboard navigation (arrow keys, Enter, Escape).
 * Allows free-text entry when no match found.
 */
export function createAirportComboBox(config) {
  const {
    name,
    label,
    aircraftId = null,
    onSelect,
    value = '',
    airports = [],
    favourites = [],
  } = config;

  let dropdownOpen = false;
  let activeIndex = -1; // keyboard navigation index
  let noMatchShown = false; // tracks whether "No matching airports" is displayed

  // ── Root container ─────────────────────────────────────────────────────
  const container = document.createElement('div');
  container.className = 'airport-combo-box';
  container.style.position = 'relative';

  // Label
  const labelEl = document.createElement('label');
  labelEl.className = 'airport-combo-box__label';
  labelEl.setAttribute('for', `airport-cb-${name}`);
  labelEl.textContent = label;
  container.appendChild(labelEl);

  // Input
  const input = document.createElement('input');
  input.type = 'text';
  input.id = `airport-cb-${name}`;
  input.name = name;
  input.className = 'airport-combo-box__input';
  input.placeholder = 'Type ICAO code or airport name…';
  input.setAttribute('autocomplete', 'off');
  input.setAttribute('role', 'combobox');
  input.setAttribute('aria-expanded', 'false');
  input.setAttribute('aria-autocomplete', 'list');
  if (value) input.value = value;
  container.appendChild(input);

  // Dropdown
  const dropdown = document.createElement('div');
  dropdown.className = 'airport-combo-box__dropdown';
  dropdown.setAttribute('role', 'listbox');
  dropdown.style.display = 'none';
  container.appendChild(dropdown);

  // Save prompt (shown when user blurs with no matching airports)
  const savePrompt = document.createElement('div');
  savePrompt.className = 'airport-combo-box__save-prompt';
  savePrompt.style.display = 'none';
  container.appendChild(savePrompt);

  // ── Helpers ────────────────────────────────────────────────────────────

  function showDropdown() {
    dropdown.style.display = '';
    dropdownOpen = true;
    input.setAttribute('aria-expanded', 'true');
  }

  function hideDropdown() {
    dropdown.style.display = 'none';
    dropdownOpen = false;
    activeIndex = -1;
    input.setAttribute('aria-expanded', 'false');
  }

  function getItems() {
    return dropdown.querySelectorAll('.airport-combo-box__item');
  }

  function setActiveItem(index) {
    const items = getItems();
    // Clear previous active
    items.forEach((item) => item.classList.remove('airport-combo-box__item--active'));
    activeIndex = index;
    if (index >= 0 && index < items.length) {
      items[index].classList.add('airport-combo-box__item--active');
      items[index].scrollIntoView({ block: 'nearest' });
    }
  }

  /**
   * Build the initial dropdown content shown on focus (no query typed).
   */
  function getInitialItems() {
    // If aircraftId is set and we have favourites, show them
    if (aircraftId && favourites.length > 0) {
      return { items: favourites.slice(0, MAX_RESULTS), total: favourites.length, isFavourites: true };
    }
    // Otherwise show first 10 alphabetically by ICAO code
    const sorted = [...airports].sort((a, b) =>
      (a.icao_code || '').localeCompare(b.icao_code || ''),
    );
    return { items: sorted.slice(0, MAX_RESULTS), total: sorted.length, isFavourites: false };
  }

  /**
   * Render the dropdown with a list of airports.
   */
  function renderDropdown(items, total, query, isFavourites = false) {
    dropdown.innerHTML = '';
    activeIndex = -1;

    if (items.length === 0 && query) {
      // No matching airports
      noMatchShown = true;
      const noMatch = document.createElement('div');
      noMatch.className = 'airport-combo-box__no-match';
      noMatch.textContent = 'No matching airports';
      dropdown.appendChild(noMatch);
      showDropdown();
      return;
    }

    noMatchShown = false;

    if (items.length === 0) {
      hideDropdown();
      return;
    }

    // Group label
    if (isFavourites) {
      const groupLabel = document.createElement('div');
      groupLabel.className = 'airport-combo-box__group-label';
      groupLabel.textContent = 'Favourites';
      dropdown.appendChild(groupLabel);
    }

    // Airport items
    items.forEach((airport) => {
      const item = document.createElement('div');
      item.className = 'airport-combo-box__item';
      item.setAttribute('role', 'option');
      item.textContent = formatAirport(airport);
      item.addEventListener('mousedown', (e) => {
        e.preventDefault();
        selectAirport(airport);
      });
      dropdown.appendChild(item);
    });

    // Hint when more results exist
    if (total > MAX_RESULTS) {
      const hint = document.createElement('div');
      hint.className = 'airport-combo-box__hint';
      hint.textContent = 'Type more to narrow results';
      dropdown.appendChild(hint);
    }

    showDropdown();
  }

  /**
   * Select an airport: populate input, close dropdown, fire callback.
   */
  function selectAirport(airport) {
    input.value = airport.icao_code || airport.name;
    hideDropdown();
    hideSavePrompt();
    onSelect(airport);
  }

  /**
   * Show the inline save prompt for an unknown airport name.
   */
  function showSavePrompt(enteredText) {
    savePrompt.innerHTML = '';
    savePrompt.style.display = '';

    const message = document.createElement('span');
    message.className = 'airport-combo-box__save-prompt-message';
    message.textContent = `Airport not found. Save '${enteredText}' as a new airport?`;
    savePrompt.appendChild(message);

    const btnGroup = document.createElement('span');
    btnGroup.className = 'airport-combo-box__save-prompt-actions';

    const saveBtn = document.createElement('button');
    saveBtn.type = 'button';
    saveBtn.className = 'airport-combo-box__save-prompt-save';
    saveBtn.textContent = 'Save';
    saveBtn.addEventListener('click', async () => {
      saveBtn.disabled = true;
      cancelBtn.disabled = true;
      saveBtn.textContent = 'Saving…';
      try {
        const newAirport = await createAirport({ icao_code: null, name: enteredText });
        airports.push(newAirport);
        // Brief success flash before hiding
        savePrompt.innerHTML = '';
        const successMsg = document.createElement('span');
        successMsg.className = 'airport-combo-box__save-prompt-success';
        successMsg.textContent = `✓ '${enteredText}' saved.`;
        savePrompt.appendChild(successMsg);
        setTimeout(() => {
          selectAirport(newAirport);
        }, 800);
      } catch (err) {
        // Show error in the prompt area
        saveBtn.disabled = false;
        cancelBtn.disabled = false;
        saveBtn.textContent = 'Save';
        savePrompt.innerHTML = '';
        const errorMsg = document.createElement('span');
        errorMsg.className = 'airport-combo-box__save-prompt-error';
        errorMsg.textContent = `Failed to save: ${err.message || 'Unknown error'}`;
        savePrompt.appendChild(errorMsg);
        // Re-add buttons so user can retry
        savePrompt.appendChild(btnGroup);
      }
    });
    btnGroup.appendChild(saveBtn);

    const cancelBtn = document.createElement('button');
    cancelBtn.type = 'button';
    cancelBtn.className = 'airport-combo-box__save-prompt-cancel';
    cancelBtn.textContent = 'Cancel';
    cancelBtn.addEventListener('click', () => {
      hideSavePrompt();
    });
    btnGroup.appendChild(cancelBtn);

    savePrompt.appendChild(btnGroup);
  }

  /**
   * Hide the save prompt.
   */
  function hideSavePrompt() {
    savePrompt.style.display = 'none';
    savePrompt.innerHTML = '';
  }

  /**
   * Update the dropdown based on the current input value.
   */
  function updateDropdown() {
    const query = input.value.trim();

    if (!query) {
      // Show initial items (favourites or first 10)
      const { items, total, isFavourites } = getInitialItems();
      renderDropdown(items, total, '', isFavourites);
      return;
    }

    // Filter all airports by query
    const matched = filterAirports(airports, query);
    const total = matched.length;
    const capped = matched.slice(0, MAX_RESULTS);
    renderDropdown(capped, total, query);
  }

  // ── Event handlers ─────────────────────────────────────────────────────

  // Focus: show dropdown with initial items
  input.addEventListener('focus', () => {
    updateDropdown();
  });

  // Input: filter on every keystroke
  input.addEventListener('input', () => {
    updateDropdown();
  });

  // Keyboard navigation
  input.addEventListener('keydown', (e) => {
    if (!dropdownOpen) return;

    const items = getItems();
    const itemCount = items.length;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const next = activeIndex < itemCount - 1 ? activeIndex + 1 : 0;
      setActiveItem(next);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prev = activeIndex > 0 ? activeIndex - 1 : itemCount - 1;
      setActiveItem(prev);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (activeIndex >= 0 && activeIndex < itemCount) {
        items[activeIndex].dispatchEvent(new MouseEvent('mousedown', { bubbles: true }));
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      hideDropdown();
    }
  });

  // Blur: show save prompt if no matching airports and input has value
  input.addEventListener('blur', () => {
    const enteredText = input.value.trim();
    if (noMatchShown && enteredText) {
      showSavePrompt(enteredText);
    }
    hideDropdown();
  });

  // Close on outside click
  document.addEventListener('mousedown', (e) => {
    if (dropdownOpen && !container.contains(e.target)) {
      hideDropdown();
    }
  });

  return container;
}
